const ACTION_VERBS = {
  CREATE: "Created",
  UPDATE: "Updated",
  DELETE: "Deleted",
  CHANGE: "Changed",
  ASSIGN: "Assigned",
  REMOVE: "Removed",
  APPROVE: "Approved",
  REJECT: "Rejected",
  CANCEL: "Cancelled",
  UPLOAD: "Uploaded",
  ADD: "Added",
  SEND: "Sent",
  APPLY: "Applied",
  LOGIN: "Logged in",
  RESET: "Reset",
  REVIEW: "Reviewed",
};

const FIELD_OVERRIDES = {
  _id: "ID",
  id: "ID",
  entityId: "Entity ID",
  ownerId: "Owner",
  userId: "User",
};

export const humanize = (value = "") =>
  String(value)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[._-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
export const fieldLabel = (path = "") =>
  path
    .split(".")
    .map((part) => FIELD_OVERRIDES[part] || humanize(part))
    .join(" › ");

export const readableAction = (action = "", entityType = "") => {
  const parts = String(action).toUpperCase().split("_").filter(Boolean);
  const isBulk = parts[0] === "BULK";
  const verbIndex = isBulk ? 1 : 0;
  const verb =
    ACTION_VERBS[parts[verbIndex]] || humanize(parts[verbIndex] || "Activity");
  const subject = parts.slice(verbIndex + 1).join("_") || entityType;
  return `${isBulk ? "Bulk " : ""}${verb}${subject ? ` ${humanize(subject).toLowerCase()}` : ""}`.trim();
};

export const actionCategory = (action = "") => {
  const upper = String(action).toUpperCase();
  if (upper.includes("BULK")) return "bulk";
  if (/DELETE|REMOVE|REJECT|CANCEL/.test(upper)) return "delete";
  if (/CREATE|ADD|APPROVE|UPLOAD/.test(upper)) return "create";
  if (/UPDATE|CHANGE|ASSIGN|RESET/.test(upper)) return "update";
  if (/LOGIN/.test(upper)) return "login";
  return "neutral";
};

export const actorDetails = (user) => {
  if (user && typeof user === "object")
    return {
      name: user.name || user.email || "Deleted User",
      email: user.name ? user.email : "",
    };
  if (typeof user === "string" && user)
    return { name: "Deleted User", email: user };
  return { name: "System", email: "" };
};

// Intl formatters are costly to build; reuse one per locale.
const dateFormatters = {};
const relativeFormatters = {};

const dateFormatter = (locale) => {
  const key = locale === "ar" ? "ar-SA" : "en-US";
  dateFormatters[key] ||= new Intl.DateTimeFormat(key, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  return dateFormatters[key];
};

const relativeFormatter = (locale) => {
  const key = locale === "ar" ? "ar" : "en";
  relativeFormatters[key] ||= new Intl.RelativeTimeFormat(key, { numeric: "auto" });
  return relativeFormatters[key];
};

export const formatDate = (date, locale = "en") => {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "Unknown time";
  return dateFormatter(locale)
    .format(parsed)
    .replace(/, (?=\d{1,2}:)/, " · ");
};

export const relativeTime = (date, locale = "en") => {
  const time = new Date(date).getTime();
  if (!Number.isFinite(time)) return "";
  const seconds = Math.round((time - Date.now()) / 1000);
  const units = [
    ["year", 31536000],
    ["month", 2592000],
    ["day", 86400],
    ["hour", 3600],
    ["minute", 60],
  ];
  const [unit, divisor] = units.find(
    ([, size]) => Math.abs(seconds) >= size,
  ) || ["second", 1];
  return relativeFormatter(locale).format(Math.round(seconds / divisor), unit);
};

export const formatValue = (value) => {
  if (value === undefined || value === null || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value))
    return value.length
      ? value
          .map((item) =>
            typeof item === "object" ? JSON.stringify(item) : String(item),
          )
          .join(", ")
      : "Empty list";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

export const entityIdOf = (log) =>
  log?.data?.entityId || log?.entityId || log?.data?.id || log?.data?._id || "";
